import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { PlainButton, useIsMobile } from 'components/v3';
import Glyph from '../Glyph';
import { ORANGE, sx } from '../sx';

// The saved-view tabs along the top of the table card (design ViewTabs):
// the built-in Default and every saved view, "New view", and the Columns
// button with the active view's column count.

const Strip = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: ${(p) => (p.$mobile ? '50px' : '54px')};
  padding: ${(p) => (p.$mobile ? '0 8px 0 4px' : '0 12px 0 8px')};
  border-bottom: 1px solid ${(p) => sx(p.theme).line};
`;

const Tabs = styled.div`
  flex: 1;
  min-width: 0;
  align-self: stretch;
  display: flex;
  align-items: center;
  gap: 2px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled(PlainButton)`
  position: relative;
  flex: none;
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 ${(p) => (p.$mobile ? '12px' : '14px')};
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(p) => (p.$on ? sx(p.theme).text : sx(p.theme).label)};
  transition: color 120ms ease;

  svg {
    color: ${(p) => (p.$on ? ORANGE : sx(p.theme).label)};
  }

  &:hover {
    color: ${(p) => sx(p.theme).text};
  }

  &::after {
    content: '';
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: -1px;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: ${(p) => (p.$on ? ORANGE : 'transparent')};
  }
`;

const Right = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NewView = styled(PlainButton)`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px dashed ${(p) => sx(p.theme).line2};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(p) => sx(p.theme).label};

  &:hover {
    color: ${(p) => sx(p.theme).otext};
    border-color: ${(p) => sx(p.theme).tintB};
    background: ${(p) => sx(p.theme).tint};
  }
`;

const Columns = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 10px 0 12px;
  border-radius: 9px;
  border: 1px solid ${(p) => sx(p.theme).line2};
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  color: ${(p) => sx(p.theme).text2};

  &:hover {
    border-color: ${ORANGE};
    color: ${ORANGE};
  }
`;

const Count = styled.span`
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  box-sizing: border-box;
  border-radius: 999px;
  background: ${(p) => sx(p.theme).fill};
  color: ${(p) => sx(p.theme).text2};
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-variant-numeric: tabular-nums;
`;

const ViewTabs = ({
  views,
  activeId,
  columnCount,
  onPick,
  onNew,
  onColumns,
}) => {
  const isMobile = useIsMobile();
  return (
    <Strip $mobile={isMobile} data-tour="view-tabs">
      <Tabs role="tablist" aria-label="Table views">
        {views.map((v) => (
          <Tab
            key={v.id}
            role="tab"
            $mobile={isMobile}
            $on={v.id === activeId}
            aria-selected={v.id === activeId}
            data-help="view-tab"
            onClick={() => onPick(v.id)}
          >
            <Glyph name={v.builtIn ? 'table' : 'bookmark'} size={14} />
            {v.name}
          </Tab>
        ))}
      </Tabs>
      <Right>
        <NewView onClick={onNew} aria-label="New view" data-help="new-view">
          <Glyph name="plus" size={14} />
          {isMobile ? null : 'New view'}
        </NewView>
        <Columns
          onClick={onColumns}
          data-tour="columns-button"
          aria-label={`Columns (${columnCount})`}
        >
          <Glyph name="columns" size={15} />
          {isMobile ? null : 'Columns'}
          <Count aria-hidden="true">{columnCount}</Count>
        </Columns>
      </Right>
    </Strip>
  );
};

ViewTabs.propTypes = {
  views: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      builtIn: PropTypes.bool,
    })
  ).isRequired,
  activeId: PropTypes.string.isRequired,
  columnCount: PropTypes.number.isRequired,
  onPick: PropTypes.func.isRequired,
  onNew: PropTypes.func.isRequired,
  onColumns: PropTypes.func.isRequired,
};

export default ViewTabs;
